<template>
  <v-flex xs12>
    <v-card>
      <v-flex xs12 px-4 py-4 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-layout row wrap align-center class="pt-3 px-2">
          <v-flex xs12 md4 lg3 px-3>
            <v-autocomplete
              :label="$lang.actions.selectCompany"
              :items="mappedCompanies"
              :loading="companyLoading"
              v-model="data.companyId"
              required
              :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
            />
          </v-flex>
          <v-flex xs12 md8 lg9 px-3>
            <v-select
              :label="$lang.labels.pricingGroups"
              :placeholder="$lang.placeholders.pricingGroups"
              multiple
              clearable
              :items="mappedGroups"
              v-model="data.pricingGroups"
              :disabled="mappedGroups.length <= 0"
              required
              :rules="[data.pricingGroups.length > 0 || `${$lang.errors.missingPricingGroups}`]"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap align-end class="py-1 px-2">
          <v-flex xs12 sm6 md4 lg3 px-3>
            <v-select
              :items="years"
              :label="$lang.actions.selectYear"
              v-model="data.year"
              required
              :rules="[v => !!v || `${$lang.errors.missingYear}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 lg3 px-3>
            <v-select
              clearable
              :items="months"
              :label="$lang.actions.selectMonth"
              v-model="data.month"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="px-3">
          <v-btn color="primary" :disabled="!valid" class="mb-2 mt-0 mx-2" @click="fetchInvoicesforCompany()">{{$lang.labels.search}}</v-btn>
          <v-btn color="primary" class="mb-2 mt-0 mx-2" @click="openOverview()" :loading="overviewloading">{{$lang.labels.buildOverview}}</v-btn>
          <v-btn color="primary" class="mb-2 mt-0 mx-2" @click="showBuildModal = true">{{$lang.labels.generateInvoice}}</v-btn>
        </v-layout>
        <v-card-title class="pb-2 pt-0 px-0 layout row wrap" >
          <v-spacer />
          <v-text-field
            v-if="show"
            class="min-w max-w mx-4 pb-2"
            v-model="search"
            append-icon="search"
            :label="$lang.labels.search"
            hide-details
          />
        </v-card-title>
      </v-form>
      <v-data-table
        v-if="show"
        :headers="headers"
        :items="invoices"
        :search="search"
        :loading="invoices.length === 0"
        :rows-per-page-items="[50, 100, 250, 500, { text:'All' , value: -1 }]"
      >
        <v-progress-linear height="6" slot="progress" color="primary" indeterminate />
        <template slot="items" slot-scope="props">
          <td class="text-xs-left">{{ props.item.companyName }}</td>
          <td class="text-xs-left">{{ props.item.pricingGroupName }}</td>
          <td class="text-xs-right">{{ props.item.totalAmount }}</td>
          <td class="text-xs-left">{{ formatDate(props.item.createdOn) }}</td>
          <td class="text-xs-left">
            <v-btn
              color="primary"
              class="mx-0"
              @click="showDetails(props.item.id)"
            >
              {{$lang.actions.view}}
            </v-btn>
          </td>
        </template>
        <v-alert slot="no-results" :value="true" color="warning" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card>
    <v-dialog v-if="showOverviewModal" v-model="showOverviewModal" max-width="992">
      <overview-modal
        :companies="companies"
        :pricingGroups="overviewConfig"
        :months="months"
        :years="years"
        :selectedMonth="data.month"
        :selectedYear="data.year"
        @close="showOverviewModal = false"/>
    </v-dialog>
    <v-dialog v-if="showBuildModal" v-model="showBuildModal" max-width="450">
      <build-modal
        :months="months"
        :years="years"
        :selectedMonth="data.month"
        :selectedYear="data.year"
        @close="showBuildModal = false"/>
    </v-dialog>
    <v-dialog v-if="detailsModal" v-model="detailsModal" max-width="992">
      <details-modal :invoiceId="invoiceId" @close="detailsModal = false"/>
    </v-dialog>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      overviewModal: () => import('../modals/InvoiceOverviewModal'),
      detailsModal: () => import('../modals/InvoiceDetails'),
      buildModal: () => import('../modals/InvoiceGenerateModal'),
    },
    data() {
      return {
        err: '',
        search: '',
        show: false,
        language: this.$cookie.get('lang') || 'en',
        companies: [],
        valid: false,
        currency: null,
        showOverviewModal: false,
        showBuildModal: false,
        data: {
          companyId: null,
          pricingGroups: [],
          month: DateTime.local().minus({ months: 1 }).month,
          year: DateTime.local().minus({ months: 1 }).year,
        },
        companyLoading: true,
        headers: [
          { text: this.$lang.labels.companyName, value: 'companyName', align: 'left' },
          { text: this.$lang.header.pricingGroupName, value: 'pricingGroupName', align: 'left' },
          { text: this.$lang.header.totalAmount, value: 'totalAmount', align: 'right' },
          { text: this.$lang.labels.created, value: 'created', align: 'left' },
          { text: this.$lang.labels.actions, value: 'actions', align: 'left', sortable: false },
        ],
        invoices: [],
        allPricingGroups: [],
        months: [
          {value: 1, text: this.$lang.labels.january},
          {value: 2, text: this.$lang.labels.february},
          {value: 3, text: this.$lang.labels.march},
          {value: 4, text: this.$lang.labels.april},
          {value: 5, text: this.$lang.labels.may},
          {value: 6, text: this.$lang.labels.june},
          {value: 7, text: this.$lang.labels.july},
          {value: 8, text: this.$lang.labels.august},
          {value: 9, text: this.$lang.labels.september},
          {value: 10, text: this.$lang.labels.october},
          {value: 11, text: this.$lang.labels.november},
          {value: 12, text: this.$lang.labels.december},
        ],
        overviewConfig: [],
        overviewloading: false,
        detailsModal: false,
        invoiceId: null,
      };
    },
    watch: {
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
      'data.companyId':function(val) {
        this.$cookie.set('companyedit', val, { expires: '8h' });
        this.data.pricingGroups = [];
        const company = this.companies.find(company => company.id === val);
        if (company.currency) {
          this.allPricingGroups = [];
          this.fetchPricingGroups(company.currency);
        }
        if (!company) return;
      },
    },
    computed: {
      years() {
        let array = [];
        let x = 2018;
        for (x; x <= DateTime.local().year; x++) {
          array.push(x);
        }
        return array;
      },
      mappedGroups() {
        return this.allPricingGroups.map(group => ({
          text: group.name,
          value: group.id,
        }));
      },
      mappedCompanies() {
        return this.companies.map(company => ({
          text: `${company.name} (${company.companyStatus})`,
          value: company.id,
        }));
      },
    },
    methods: {
      showDetails(id) {
        this.invoiceId = id;
        this.detailsModal = true;
      },
      async openOverview() {
        this.overviewloading = true;
        this.overviewConfig = [];
        await Promise.all(this.companies.map(async (company) => this.fetchPricingGroups(company.currency, true)));
        this.overviewloading = false;
        this.showOverviewModal = true;
      },
      fetchPricingGroups(currency, isOverview = false) {
        return new Promise((resolve) => {
          this.$http.post('pricinggroup/getall', { currency })
            .then((res) => {
              if (res.body.status === 'Success') {
                if (isOverview) {
                  this.overviewConfig.push(...res.body.pricingGroups)
                } else {
                  this.allPricingGroups = res.body.pricingGroups;
                }
                resolve();
                return;
              }
              resolve();
              this.err = res.body.statusText;
            }, (err) => {
              this.err = err.body.statusText || err.body.message;
              resolve();
            });
        });
      },
      init() {
        return new Promise((resolve) => {
            this.$http.get('company?detailed=true')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd');
      },
      fetchInvoicesforCompany() {
        this.show = true;
        this.invoices = [];
        this.$http.post('pricinggroupinvoice/getall', this.data)
        .then((res) => {
          this.companyLoading = false;
          if (res.body.status === 'Success') {
            this.invoices = res.body.pricingGroupInvoices;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.companyLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
      },
    },
    created() {
      this.init()
        .then(() => {
          const edit = this.$cookie.get('companyedit');
          if (edit) this.data.companyId = edit;
        });
    },
  };
</script>



// WEBPACK FOOTER //
// Invoices.vue?88393ffa